import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import i18n from '@shared/i18n';
import {
  getReminders,
  ensureDefaultReminders,
  type LocalReminder,
} from './localReminders';

export const QUIET_HOURS_KEY = 'notification_quiet_hours';
const MAX_OCCURRENCES = 30;

export type QuietHours = { start: string; end: string } | null;

export async function getQuietHours(): Promise<QuietHours> {
  const { value } = await Preferences.get({ key: QUIET_HOURS_KEY });
  if (!value) return null;
  try {
    return JSON.parse(value) as QuietHours;
  } catch {
    return null;
  }
}

export async function setQuietHours(hours: QuietHours): Promise<void> {
  if (hours === null) {
    await Preferences.remove({ key: QUIET_HOURS_KEY });
  } else {
    await Preferences.set({ key: QUIET_HOURS_KEY, value: JSON.stringify(hours) });
  }
}

function parseHHMM(timeStr: string): { h: number; m: number } {
  const parts = timeStr.split(':');
  return { h: Number(parts[0]) || 0, m: Number(parts[1]) || 0 };
}

export function isInQuietHours(date: Date, quietHours: QuietHours): boolean {
  if (!quietHours) return false;
  const { h: sh, m: sm } = parseHHMM(quietHours.start);
  const { h: eh, m: em } = parseHHMM(quietHours.end);
  const total = date.getHours() * 60 + date.getMinutes();
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  // When startMin > endMin the window wraps past midnight (e.g. 22:00 → 07:00)
  return startMin <= endMin
    ? total >= startMin && total < endMin
    : total >= startMin || total < endMin;
}

// djb2 hash for deterministic, integer notification IDs.
// Re-scheduling with identical inputs produces the same ID so cancel-then-reschedule
// is idempotent at the OS notification store level.
export function notificationId(reminderId: string, occurrenceIndex: number): number {
  const str = `${reminderId}:${occurrenceIndex}`;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0x7fffffff;
  }
  return Math.abs(hash) || 1;
}

const DAY_TO_JS: Record<string, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
};

function expandDaily(
  reminder: LocalReminder,
  quietHours: QuietHours,
  now: Date,
): Date[] {
  const dates: Date[] = [];
  const times = reminder.schedule.times ?? ['08:00'];
  for (let day = 0; day < 90 && dates.length < MAX_OCCURRENCES; day++) {
    for (const timeStr of times) {
      if (dates.length >= MAX_OCCURRENCES) break;
      const { h, m } = parseHHMM(timeStr);
      const d = new Date(now);
      d.setDate(d.getDate() + day);
      d.setHours(h, m, 0, 0);
      if (d > now && !isInQuietHours(d, quietHours)) dates.push(d);
    }
  }
  return dates;
}

function expandWeekly(
  reminder: LocalReminder,
  quietHours: QuietHours,
  now: Date,
): Date[] {
  const dates: Date[] = [];
  const times = reminder.schedule.times ?? ['08:00'];
  const days = reminder.schedule.days ?? ['mon'];
  const allowed = new Set(days.map((d) => DAY_TO_JS[d]));
  for (let day = 0; day < 365 && dates.length < MAX_OCCURRENCES; day++) {
    const base = new Date(now);
    base.setDate(base.getDate() + day);
    if (!allowed.has(base.getDay())) continue;
    for (const timeStr of times) {
      if (dates.length >= MAX_OCCURRENCES) break;
      const { h, m } = parseHHMM(timeStr);
      const d = new Date(base);
      d.setHours(h, m, 0, 0);
      if (d > now && !isInQuietHours(d, quietHours)) dates.push(d);
    }
  }
  return dates;
}

function expandInterval(
  reminder: LocalReminder,
  quietHours: QuietHours,
  now: Date,
): Date[] {
  const dates: Date[] = [];
  const everyMs = (reminder.schedule.every_minutes ?? 60) * 60_000;
  const limit = now.getTime() + 365 * 24 * 3_600_000;
  let next = new Date(now.getTime() + everyMs);
  while (dates.length < MAX_OCCURRENCES && next.getTime() < limit) {
    if (!isInQuietHours(next, quietHours)) dates.push(new Date(next));
    next = new Date(next.getTime() + everyMs);
  }
  return dates;
}

export function expandOccurrences(
  reminder: LocalReminder,
  quietHours: QuietHours,
  now = new Date(),
): Date[] {
  switch (reminder.schedule.type) {
    case 'daily':    return expandDaily(reminder, quietHours, now);
    case 'weekly':   return expandWeekly(reminder, quietHours, now);
    case 'interval': return expandInterval(reminder, quietHours, now);
    default:         return [];
  }
}

export async function cancelReminder(reminderId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const ids = Array.from({ length: MAX_OCCURRENCES }, (_, i) => ({
    id: notificationId(reminderId, i),
  }));
  await LocalNotifications.cancel({ notifications: ids }).catch(() => {/* ignore */});
}

export async function scheduleReminder(reminder: LocalReminder): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  // Cancel first so rescheduling is idempotent
  await cancelReminder(reminder.id);
  if (!reminder.enabled) return;
  const quietHours = await getQuietHours();
  const lang = i18n.language || 'en';
  const title = i18n.t(reminder.title_key, { ns: 'reminders', lng: lang });
  const body = i18n.t(reminder.body_key, {
    ns: 'reminders',
    lng: lang,
    ...(reminder.body_params ?? {}),
  });
  const occurrences = expandOccurrences(reminder, quietHours);
  if (occurrences.length === 0) return;
  const notifications = occurrences.map((date, idx) => ({
    id: notificationId(reminder.id, idx),
    title,
    body,
    schedule: { at: date },
    extra: { reminder_id: reminder.id, deep_link: reminder.deep_link ?? '' },
    channelId: 'reminders',
  }));
  await LocalNotifications.schedule({ notifications });
}

export async function syncScheduler(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const reminders = await getReminders();
  await Promise.all(
    reminders.map((r) => (r.enabled ? scheduleReminder(r) : cancelReminder(r.id))),
  );
}

// Call once on app start. Seeds defaults, syncs the OS schedule, and registers
// a language-change handler so notifications are re-created in the new locale.
export async function initScheduler(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await ensureDefaultReminders();
  await syncScheduler();
  i18n.on('languageChanged', () => {
    void syncScheduler();
  });
}
